/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Slider,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useToast, { ToastType } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import { Barrier } from '../../types/Barrier';
import useBarriersService from '../../services/administration/useBarriersService';
import { DialogMode } from '../../types/DialogTypes';
import { useDialogForm } from '../../hooks/useDialogForm';

export type BarrierDialogFields = Omit<Barrier, 'locationid' | 'id'>;

interface BarrierDialogProps {
    open: boolean;
    onClose: () => void;
    mode?: DialogMode;
    initialValues?: BarrierDialogFields;
    barrierId?: number;
};

const REQUIRED_FIELDS = [
    'name',
    'phonenumber',
] as const satisfies ReadonlyArray<keyof BarrierDialogFields>;


const DEFAULT_FIELDS: BarrierDialogFields = {
    name: '',
    phonenumber: '',
    openatopening: false,
    openoffsetseconds: 0,
    closeatclosing: false,
    closeoffsetseconds: 0,
    opencode: '',
    closecode: '',
};

function BarrierDialog({
    open,
    onClose,
    mode = DialogMode.CREATE,
    initialValues,
    barrierId,
}: BarrierDialogProps) {
    const [loading, setLoading] = useState(false);

    const { t } = useTranslation("common");
    const { activeLocation } = useAuth();
    const { showToast } = useToast();
    const { createBarrier, updateBarrier } = useBarriersService();

    const activeLocationId = activeLocation?.id ?? null;

    const normalizedInitialValues = useMemo(() => {
        if (!initialValues) return undefined;
        return {
            ...initialValues,
            openoffsetseconds: initialValues.openoffsetseconds / 60,
            closeoffsetseconds: initialValues.closeoffsetseconds / 60,
        };
    }, [initialValues]);

    const {
        fields,
        handleChange,
        validateForm,
        setFieldTouched,
        shouldShowFieldError,
    } = useDialogForm<BarrierDialogFields>({
        config: {
            requiredFields: REQUIRED_FIELDS,
            defaultValues: DEFAULT_FIELDS,
            additionalValidation: () => activeLocationId !== null,
        },
        initialValues: normalizedInitialValues,
        isOpen: open,
    });

    const isViewMode = mode === DialogMode.VIEW;
    const isEditMode = mode === DialogMode.EDIT;

    const dialogTitle = useMemo(() => {
        switch (mode) {
            case DialogMode.VIEW:
                return t('administration.locationAdministration.barrierDialog.viewBarrier');
            case DialogMode.EDIT:
                return t('administration.locationAdministration.barrierDialog.editBarrier');
            case DialogMode.CREATE:
            default:
                return t('administration.locationAdministration.barrierDialog.addBarrier');
        }
    }, [mode, t]);

    // custom step function: 1 min for first 5 min, then 5 min
    const getSliderMarks = () => {
        const marks = [];
        // -60 to -5: every 5 minutes
        for (let i = -60; i <= -5; i += 5) {
            marks.push({ value: i });
        }
        // -5 to +5: every 1 minute
        for (let i = -5; i <= 5; i += 1) {
            marks.push({ value: i });
        }
        // +5 to +60: every 5 minutes
        for (let i = 10; i <= 60; i += 5) {
            marks.push({ value: i });
        }
        return marks;
    };

    const formatSliderValue = (value: number): string => {
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value} ${t('common.minutes.short')}`;
    };

    const validateFields = () => {
        if (!validateForm()) {
            return false;
        }

        if (!activeLocationId) {
            showToast({
                message: t('common.unknownError'),
                type: ToastType.ERROR,
            });
            return false;
        }
        return true;
    };

    const handleCreateBarrier = async () => {
        if (!validateFields()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...fields,
                openoffsetseconds: fields.openoffsetseconds * 60,
                closeoffsetseconds: fields.closeoffsetseconds * 60,
            };
            await createBarrier(payload);
            showToast({
                message: t('administration.locationAdministration.barrierDialog.barrierCreatedSuccessfully'),
                type: ToastType.SUCCESS,
            });
            onClose();
        } catch {
            showToast({
                message: t('common.unknownError'),
                type: ToastType.ERROR,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditBarrier = async () => {
        if (!validateFields()) {
            return;
        }

        const payload = {
            id: barrierId!,
            ...fields,
            openoffsetseconds: fields.openoffsetseconds * 60,
            closeoffsetseconds: fields.closeoffsetseconds * 60,
        };

        if (!barrierId) {
            showToast({
                message: t('common.unknownError'),
                type: ToastType.ERROR,
            });
            return;
        }

        setLoading(true);
        try {
            await updateBarrier(payload);
            showToast({
                message: t('administration.locationAdministration.barrierDialog.barrierEditedSuccessfully'),
                type: ToastType.SUCCESS,
            });
            onClose();
        } catch {
            showToast({
                message: t('common.unknownError'),
                type: ToastType.ERROR,
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
                    <TextField
                        label={t('administration.locationAdministration.barrierDialog.barrierName')}
                        name="name"
                        value={fields.name}
                        onChange={e => handleChange('name', e.target.value)}
                        onBlur={() => setFieldTouched('name')}
                        error={shouldShowFieldError('name')}
                        helperText={shouldShowFieldError('name') ? t('common.completeMandatoryFields') : ' '}
                        disabled={isViewMode}
                        fullWidth
                        variant="outlined"
                        required
                    />
                    <TextField
                        label={t('administration.locationAdministration.barrierDialog.barrierPhoneNumber')}
                        name="phonenumber"
                        value={fields.phonenumber}
                        onChange={e => handleChange('phonenumber', e.target.value)}
                        onBlur={() => setFieldTouched('phonenumber')}
                        error={shouldShowFieldError('phonenumber')}
                        helperText={shouldShowFieldError('phonenumber') ? t('common.completeMandatoryFields') : ' '}
                        fullWidth
                        variant="outlined"
                        disabled={isViewMode}
                        required
                    />
                    <TextField
                        label={t('administration.locationAdministration.barrierDialog.openCode')}
                        name="opencode"
                        value={fields.opencode}
                        onChange={e => handleChange('opencode', e.target.value)}
                        fullWidth
                        variant="outlined"
                        disabled={isViewMode}
                    />
                    <TextField
                        label={t('administration.locationAdministration.barrierDialog.closeCode')}
                        name="closecode"
                        value={fields.closecode}
                        onChange={e => handleChange('closecode', e.target.value)}
                        fullWidth
                        variant="outlined"
                        disabled={isViewMode}
                    />

                    {/* Opens at schedule start */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={fields.openatopening}
                                    onChange={e => handleChange('openatopening', e.target.checked)}
                                    disabled={isViewMode}
                                />
                            }
                            label={t('administration.locationAdministration.barrierDialog.openAtOpen')}
                        />
                        {fields.openatopening && (
                            <Box sx={{ px: 2, mt: 1 }}>
                                <Slider
                                    value={fields.openoffsetseconds}
                                    onChange={(_, value) => {
                                        handleChange('openoffsetseconds', value as number);
                                    }}
                                    min={-60}
                                    max={60}
                                    marks={getSliderMarks()}
                                    step={null}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={formatSliderValue}
                                    disabled={isViewMode}
                                />
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    {(() => {
                                        const offset = fields.openoffsetseconds;
                                        if (offset === 0) {
                                            return t('administration.locationAdministration.barrierDialog.openAtTime');
                                        } else if (offset === -1) {
                                            return t('administration.locationAdministration.barrierDialog.openMinute1Before');
                                        } else if (offset === 1) {
                                            return t('administration.locationAdministration.barrierDialog.openMinute1After');
                                        } else if (offset < 0) {
                                            return t('administration.locationAdministration.barrierDialog.openMinutesNBefore',
                                                { count: Math.abs(offset) }
                                            );
                                        } else {
                                            return t('administration.locationAdministration.barrierDialog.openMinutesNAfter',
                                                { count: offset }
                                            );
                                        }
                                    })()}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Closes at schedule end */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={fields.closeatclosing}
                                    onChange={e => handleChange('closeatclosing', e.target.checked)}
                                    disabled={isViewMode}
                                />
                            }
                            label={t('administration.locationAdministration.barrierDialog.closeAtClose')}
                        />
                        {fields.closeatclosing && (
                            <Box sx={{ px: 2, mt: 1 }}>
                                <Slider
                                    value={fields.closeoffsetseconds}
                                    onChange={(_, value) => {
                                        handleChange('closeoffsetseconds', value as number);
                                    }}
                                    min={-60}
                                    max={60}
                                    marks={getSliderMarks()}
                                    step={null}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={formatSliderValue}
                                    disabled={isViewMode}
                                />
                                <Typography variant="body2" color="text.secondary" textAlign="center" mt={0.5}>
                                    {(() => {
                                        const offset = fields.closeoffsetseconds;
                                        if (offset === 0) {
                                            return t('administration.locationAdministration.barrierDialog.closeAtTime');
                                        } else if (offset === -1) {
                                            return t('administration.locationAdministration.barrierDialog.closeMinute1Before');
                                        } else if (offset === 1) {
                                            return t('administration.locationAdministration.barrierDialog.closeMinute1After');
                                        } else if (offset < 0) {
                                            return t('administration.locationAdministration.barrierDialog.closeMinutesNBefore',
                                                { count: Math.abs(offset) }
                                            );
                                        } else {
                                            return t('administration.locationAdministration.barrierDialog.closeMinutesNAfter',
                                                { count: offset }
                                            );
                                        }
                                    })()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{isViewMode ? t('common.close') : t('common.cancel')}</Button>
                {!isViewMode && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={isEditMode ? handleEditBarrier : handleCreateBarrier}
                        disabled={loading}>
                        {loading ?
                            <CircularProgress size={24} /> :
                            (isEditMode ? t('common.save') : t('administration.locationAdministration.barrierDialog.addBarrier'))
                        }
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default BarrierDialog;
